import React, { useState, useEffect } from 'react';
import { EvolutionInstance, V8Login } from '../../types/admin';
import { evolutionInstancesApi } from '../../utils/admin/evolutionApi';
import './AdminStyles.css';

interface EvolutionInstanceFormProps {
  instance?: EvolutionInstance;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

// Formulário dedicado das instâncias Evolution (WhatsApp não-oficial).
// Campos diferem da Meta: nome da instância, URL base do servidor, api_key e o número conectado.
export const EvolutionInstanceForm: React.FC<EvolutionInstanceFormProps> = ({
  instance,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const isEditing = !!instance;

  const [formData, setFormData] = useState({
    instance_name: instance?.instance_name || '',
    base_url: instance?.base_url || '',
    api_key: '',
    number: instance?.number || '',
    v8_id: instance?.v8_id ? String(instance.v8_id) : '',
    support_number: instance?.support_number || '',
    support_number_premium: instance?.support_number_premium || '',
    proxy_url: instance?.proxy_url || '',
    is_active: instance?.is_active ?? true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSecret, setShowSecret] = useState(false);
  const [v8Logins, setV8Logins] = useState<V8Login[]>([]);

  // Carrega os V8 logins disponíveis para o select (igual ao WhatsApp oficial, o número precisa de um V8).
  useEffect(() => {
    let active = true;
    evolutionInstancesApi.listV8Logins()
      .then(list => { if (active) setV8Logins(list); })
      .catch(() => { if (active) setV8Logins([]); });
    return () => { active = false; };
  }, []);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!isEditing && !formData.instance_name.trim()) {
      newErrors.instance_name = 'Nome da instância é obrigatório';
    }

    if (!isEditing || formData.base_url.trim()) {
      if (!formData.base_url.trim()) {
        newErrors.base_url = 'URL base é obrigatória';
      } else {
        try {
          new URL(formData.base_url);
        } catch {
          newErrors.base_url = 'URL base inválida';
        }
      }
    }

    if (!isEditing) {
      if (!formData.api_key.trim()) {
        newErrors.api_key = 'Chave de API (api_key) é obrigatória';
      } else if (formData.api_key.length < 10) {
        newErrors.api_key = 'Chave de API muito curta';
      }
    } else if (formData.api_key.trim() && formData.api_key.length < 10) {
      newErrors.api_key = 'Chave de API muito curta';
    }

    if (!isEditing || formData.number.trim()) {
      if (!formData.number.trim()) {
        newErrors.number = 'Número do WhatsApp é obrigatório';
      } else if (!/^\d{10,15}$/.test(formData.number.replace(/\D/g, ''))) {
        newErrors.number = 'Número inválido (10-15 dígitos)';
      }
    }

    if (!formData.v8_id) {
      newErrors.v8_id = 'Selecione um V8 para atrelar ao número';
    }

    const supportFields: Array<'support_number' | 'support_number_premium'> = ['support_number', 'support_number_premium'];
    for (const f of supportFields) {
      const v = formData[f];
      if (v && v.trim() && !/^\d{10,15}$/.test(v.replace(/\D/g, ''))) {
        newErrors[f] = 'Número inválido (10-15 dígitos)';
      }
    }

    if (formData.proxy_url && formData.proxy_url.trim()) {
      const candidate = formData.proxy_url.startsWith('http') ? formData.proxy_url : `https://${formData.proxy_url}`;
      try {
        new URL(candidate);
      } catch {
        newErrors.proxy_url = 'URL do proxy inválida';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type: inputType } = e.target;
    const finalValue = inputType === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData(prev => ({ ...prev, [name]: finalValue }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Monta o payload conforme criação/edição (só o que o backend aceita).
  const buildPayload = () => {
    if (!isEditing) {
      return {
        instance_name: formData.instance_name.trim(),
        base_url: formData.base_url.trim(),
        api_key: formData.api_key,
        number: formData.number.replace(/\D/g, ''),
        v8_id: Number(formData.v8_id),
        support_number: formData.support_number.trim() ? formData.support_number.replace(/\D/g, '') : undefined,
        support_number_premium: formData.support_number_premium.trim() ? formData.support_number_premium.replace(/\D/g, '') : undefined,
        proxy_url: formData.proxy_url.trim() || undefined,
        is_active: formData.is_active,
      };
    }
    const payload: any = {
      is_active: formData.is_active,
      proxy_url: formData.proxy_url.trim() ? formData.proxy_url.trim() : null,
      support_number: formData.support_number.trim() ? formData.support_number.replace(/\D/g, '') : null,
      support_number_premium: formData.support_number_premium.trim() ? formData.support_number_premium.replace(/\D/g, '') : null,
    };
    if (formData.base_url.trim()) payload.base_url = formData.base_url.trim();
    if (formData.number.trim()) payload.number = formData.number.replace(/\D/g, '');
    if (formData.api_key.trim()) payload.api_key = formData.api_key;
    if (formData.v8_id) payload.v8_id = Number(formData.v8_id);
    return payload;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      await onSubmit(buildPayload());
    } catch (error) {
      console.error('Erro ao salvar instância Evolution:', error);
    }
  };

  return (
    <form className="account-form" onSubmit={handleSubmit}>
      <h3>{instance ? 'Editar Instância' : 'Adicionar Instância'} Evolution</h3>

      <div className="form-group">
        <label htmlFor="instance_name">Nome da Instância *</label>
        <input
          id="instance_name"
          type="text"
          name="instance_name"
          value={formData.instance_name}
          onChange={handleChange}
          placeholder="Ex: atendimento-01"
          disabled={loading || isEditing}
          maxLength={100}
        />
        {errors.instance_name && <span className="error">{errors.instance_name}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="number">Número do WhatsApp *</label>
        <input
          id="number"
          type="tel"
          name="number"
          value={formData.number}
          onChange={handleChange}
          placeholder="Ex: 5549999999999"
          disabled={loading}
          maxLength={15}
        />
        {errors.number && <span className="error">{errors.number}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="v8_id">V8 atrelado ao número *</label>
        <select
          id="v8_id"
          name="v8_id"
          value={formData.v8_id}
          onChange={handleChange}
          disabled={loading}
        >
          <option value="">— Selecione um V8 —</option>
          {v8Logins.map(v8 => (
            <option key={v8.id} value={String(v8.id)}>{v8.login} (#{v8.id})</option>
          ))}
        </select>
        {errors.v8_id && <span className="error">{errors.v8_id}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="base_url">URL Base do Servidor Evolution *</label>
        <input
          id="base_url"
          type="text"
          name="base_url"
          value={formData.base_url}
          onChange={handleChange}
          placeholder="Ex: https://evo.seudominio.com"
          disabled={loading}
          maxLength={1000}
        />
        {errors.base_url && <span className="error">{errors.base_url}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="api_key">Chave de API (api_key) {isEditing ? '(deixe em branco para manter)' : '*'}</label>
        <input
          id="api_key"
          type={showSecret ? 'text' : 'password'}
          name="api_key"
          value={formData.api_key}
          onChange={handleChange}
          placeholder={isEditing ? '••••••••' : 'Cole a api_key da instância'}
          disabled={loading}
        />
        {errors.api_key && <span className="error">{errors.api_key}</span>}
      </div>

      <div className="form-group checkbox">
        <input
          id="showSecret"
          type="checkbox"
          checked={showSecret}
          onChange={(e) => setShowSecret(e.target.checked)}
          disabled={loading}
        />
        <label htmlFor="showSecret">Mostrar api_key</label>
      </div>

      <div className="form-group">
        <label htmlFor="support_number">Número de Suporte (Opcional)</label>
        <input
          id="support_number"
          type="tel"
          name="support_number"
          value={formData.support_number}
          onChange={handleChange}
          placeholder="Ex: 5549999999999"
          disabled={loading}
          maxLength={15}
        />
        {errors.support_number && <span className="error">{errors.support_number}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="support_number_premium">Número de Suporte Premium (Opcional)</label>
        <input
          id="support_number_premium"
          type="tel"
          name="support_number_premium"
          value={formData.support_number_premium}
          onChange={handleChange}
          placeholder="Ex: 5549999999999"
          disabled={loading}
          maxLength={15}
        />
        {errors.support_number_premium && <span className="error">{errors.support_number_premium}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="proxy_url">URL do Proxy (Opcional)</label>
        <input
          id="proxy_url"
          type="text"
          name="proxy_url"
          value={formData.proxy_url}
          onChange={handleChange}
          placeholder="Ex: proxy.example.com:8080"
          disabled={loading}
          maxLength={1000}
        />
        {errors.proxy_url && <span className="error">{errors.proxy_url}</span>}
      </div>

      <div className="form-group checkbox">
        <input
          id="is_active"
          type="checkbox"
          name="is_active"
          checked={formData.is_active}
          onChange={handleChange}
          disabled={loading}
        />
        <label htmlFor="is_active">Ativo</label>
      </div>

      <div className="form-actions">
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Salvando...' : (instance ? 'Atualizar' : 'Criar')}
        </button>
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={loading}>
          Cancelar
        </button>
      </div>
    </form>
  );
};
